import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminAssignTransactionInvoice,
  AdminAuthorizeTransaction,
  AdminFetchMissingInvoiceTransactions,
  AdminFetchPartnerPendingInvoices,
  AdminFetchPendingReviewTransactions,
  AdminRefundTransaction,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  AssignInvoiceResult,
  AssignTransactionInvoicePayload,
  AuthorizeTransactionPayload,
  FetchMissingInvoiceTransactionsPayload,
  FetchPartnerPendingInvoicesPayload,
  MissingInvoiceTransactionsPage,
  PartnerInvoice,
  PaymentsOpsTransaction,
  RefundTransactionPayload,
} from 'src/interfaces/transactionsReview'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

const encodeOptional = (value: string | null): string | undefined => {
  const trimmed = value?.trim() ?? ''

  return trimmed === '' ? undefined : encodeURIComponent(trimmed)
}

export function* onFetchMissingInvoiceTransactions(
  action: AsyncAction<FetchMissingInvoiceTransactionsPayload>,
) {
  const { partnerId, externalTransactionId, activeOnly, limit, offset } = action.payload

  try {
    // Flat envelope `{ data, total, limit, offset }`. `activeOnly` accepts only 'true'/'false';
    // false is omitted (the backend default). Text is encoded — `callApi` uses `encode: false`.
    const { payload }: { payload: MissingInvoiceTransactionsPage } = yield call(
      callSecureApi,
      Endpoints.AdminFetchMissingInvoiceTransactions,
      {
        method: 'GET',
        query: {
          partnerId: partnerId ?? undefined,
          externalTransactionId: encodeOptional(externalTransactionId),
          activeOnly: activeOnly ? 'true' : undefined,
          limit,
          offset,
        },
      },
    )

    yield put(AdminFetchMissingInvoiceTransactions.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.transactionsReview.onFetchMissingInvoiceTransactions] error',
    })

    yield put(AdminFetchMissingInvoiceTransactions.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load incoming transactions'))
    }
  }
}

export function* onFetchPendingReviewTransactions(action: AsyncAction<undefined>) {
  try {
    const { payload }: { payload: { data: PaymentsOpsTransaction[] } } = yield call(
      callSecureApi,
      Endpoints.AdminFetchPendingReviewTransactions,
      { method: 'GET' },
    )

    const rows = payload?.data ?? []

    yield put(AdminFetchPendingReviewTransactions.success(rows))

    if (action.next) {
      action.next(null, rows)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.transactionsReview.onFetchPendingReviewTransactions] error',
    })

    yield put(AdminFetchPendingReviewTransactions.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load outgoing transactions'))
    }
  }
}

export function* onFetchPartnerPendingInvoices(
  action: AsyncAction<FetchPartnerPendingInvoicesPayload>,
) {
  try {
    const { payload }: { payload: { data: PartnerInvoice[] } } = yield call(
      callSecureApi,
      Endpoints.AdminFetchPartnerPendingInvoices,
      {
        method: 'GET',
        params: [{ field: ':partnerId', value: action.payload.partnerId }],
      },
    )

    const invoices = payload?.data ?? []

    yield put(AdminFetchPartnerPendingInvoices.success(invoices))

    if (action.next) {
      action.next(null, invoices)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.transactionsReview.onFetchPartnerPendingInvoices] error',
    })

    yield put(AdminFetchPartnerPendingInvoices.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load open invoices'))
    }
  }
}

export function* onAssignTransactionInvoice(action: AsyncAction<AssignTransactionInvoicePayload>) {
  const { transactionId, invoiceId, refetch } = action.payload

  try {
    const { payload }: { payload: AssignInvoiceResult } = yield call(
      callSecureApi,
      Endpoints.AdminAssignTransactionInvoice,
      {
        method: 'POST',
        params: [{ field: ':transactionId', value: transactionId }],
        body: { invoiceId },
      },
    )

    yield put(AdminAssignTransactionInvoice.success(payload))
    yield put(AdminFetchMissingInvoiceTransactions.request(refetch))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.transactionsReview.onAssignTransactionInvoice] error',
    })

    yield put(AdminAssignTransactionInvoice.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Nothing was assigned — try again'))
    }
  }
}

export function* onRefundTransaction(action: AsyncAction<RefundTransactionPayload>) {
  const { transactionId, refetch } = action.payload

  try {
    // The transaction id travels in the body (strict DTO, `transactionId` is its only field), not
    // the path. It creates a `created` refund and moves no money; the response is the updated
    // transaction.
    const { payload } = yield call(callSecureApi, Endpoints.AdminRefundTransaction, {
      method: 'POST',
      body: { transactionId },
    })

    yield put(AdminRefundTransaction.success(payload))
    yield put(AdminFetchMissingInvoiceTransactions.request(refetch))

    if (action.next) {
      action.next(null, payload ?? true)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.transactionsReview.onRefundTransaction] error' })

    yield put(AdminRefundTransaction.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to create the refund'))
    }
  }
}

export function* onAuthorizeTransaction(action: AsyncAction<AuthorizeTransactionPayload>) {
  const { transactionId, notes } = action.payload

  try {
    const { payload } = yield call(callSecureApi, Endpoints.AdminAuthorizeTransaction, {
      method: 'POST',
      params: [{ field: ':transactionId', value: transactionId }],
      body: { notes },
    })

    yield put(AdminAuthorizeTransaction.success(payload))
    yield put(AdminFetchPendingReviewTransactions.request())

    if (action.next) {
      action.next(null, payload ?? true)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.transactionsReview.onAuthorizeTransaction] error' })

    yield put(AdminAuthorizeTransaction.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Money did not move — try again'))
    }
  }
}

export function* transactionsReviewSaga() {
  yield takeLatest(
    AdminFetchMissingInvoiceTransactions.type.REQUEST,
    onFetchMissingInvoiceTransactions,
  )
  yield takeLatest(
    AdminFetchPendingReviewTransactions.type.REQUEST,
    onFetchPendingReviewTransactions,
  )
  yield takeLatest(AdminFetchPartnerPendingInvoices.type.REQUEST, onFetchPartnerPendingInvoices)
  yield takeEvery(AdminAssignTransactionInvoice.type.REQUEST, onAssignTransactionInvoice)
  yield takeEvery(AdminRefundTransaction.type.REQUEST, onRefundTransaction)
  yield takeEvery(AdminAuthorizeTransaction.type.REQUEST, onAuthorizeTransaction)
}
